/**
 * List Card Details Panel - Slide-out panel for list card information
 *
 * Shows comprehensive card information when a card is selected in a list.
 * Similar to collection panel but with list-specific context (quantity, priority, notes).
 */
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { IoTrash } from 'react-icons/io5';

import CardAvailabilitySection from '@/components/CardDetails/CardAvailabilitySection';
import CardDetailsPanelLayout from '@/components/CardDetails/CardDetailsPanelLayout';
import CardDetailsScryfallLink from '@/components/CardDetails/CardDetailsScryfallLink';
import CardShowcase from '@/components/CardDetails/CardShowcase';
import CardShowcaseSkeleton from '@/components/CardDetails/CardShowcaseSkeleton';
import ConfirmActionModal from '@/components/CardDetails/ConfirmActionModal';
import EditionSelector from '@/components/CardDetails/EditionSelector';
import LegalitiesSection from '@/components/CardDetails/LegalitiesSection';
import PricesSection from '@/components/CardDetails/PricesSection';
import PricesSectionSkeleton from '@/components/CardDetails/PricesSectionSkeleton';
import QuickActionsBar from '@/components/CardDetails/QuickActionsBar';
import RulingsSection from '@/components/CardDetails/RulingsSection';
import RulingsSectionSkeleton from '@/components/CardDetails/RulingsSectionSkeleton';
import { getOracleId } from '@/lib/cardUtils';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { ScryfallCard } from '@/types/card';
import type { EnrichedListCard, ListPriority } from '@/types/lists';

import { useRemoveCardFromList, useUpdateListCard } from '../hooks/api';
import ListCardInfoSection from './ListCardInfoSection';

// ============================================================================
// Types
// ============================================================================

export interface ListCardDetailsPanelProps {
  /** The list card to display */
  card: EnrichedListCard | null;
  /** Name of the list for the back button */
  listName: string;
  /** Callback when panel should close */
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function ListCardDetailsPanel({ card, listName, onClose }: ListCardDetailsPanelProps): ReactElement | null {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get full card data
  const fullCardCache = useAtomValue(fullCardCacheAtom);

  // Mutations
  const updateCardFn = useUpdateListCard();
  const removeCardFn = useRemoveCardFromList();

  // scryfall_id is always present (required field)
  const scryfallId = card?.scryfall_id ?? null;

  // Get full Scryfall card data
  const scryfallCard: ScryfallCard | null = useMemo(() => {
    if (!scryfallId) return null;
    return fullCardCache.get(scryfallId) ?? null;
  }, [scryfallId, fullCardCache]);

  const isLoadingScryfall = !scryfallCard && !!scryfallId;

  // Oracle ID for integrations
  const oracleId = card?.oracle_id ?? (scryfallCard ? getOracleId(scryfallCard) : null);

  // Handler: Quantity change
  const handleQuantityChange = useCallback(
    async (quantity: number) => {
      if (!card) return;
      setIsUpdating(true);
      try {
        await updateCardFn(card.id, { quantity });
      } finally {
        setIsUpdating(false);
      }
    },
    [card, updateCardFn],
  );

  // Handler: Priority change
  const handlePriorityChange = useCallback(
    async (priority: ListPriority) => {
      if (!card) return;
      setIsUpdating(true);
      try {
        await updateCardFn(card.id, { priority });
      } finally {
        setIsUpdating(false);
      }
    },
    [card, updateCardFn],
  );

  // Handler: Notes change
  const handleNotesChange = useCallback(
    async (notes: string | null) => {
      if (!card) return;
      setIsUpdating(true);
      try {
        await updateCardFn(card.id, { notes });
      } finally {
        setIsUpdating(false);
      }
    },
    [card, updateCardFn],
  );

  // Handler: Edition change
  const handleEditionChange = useCallback(
    async (newScryfallId: string) => {
      if (!card) return;
      setIsUpdating(true);
      try {
        await updateCardFn(card.id, { scryfall_id: newScryfallId });
      } finally {
        setIsUpdating(false);
      }
    },
    [card, updateCardFn],
  );

  // Handler: Remove card
  const handleRemove = useCallback(async () => {
    if (!card) return;
    setIsUpdating(true);
    try {
      await removeCardFn(card.id);
      setShowDeleteConfirm(false);
      onClose();
    } finally {
      setIsUpdating(false);
    }
  }, [card, removeCardFn, onClose]);

  const cardName = card?.name ?? scryfallCard?.name ?? 'Loading...';

  return (
    <CardDetailsPanelLayout
      isOpen={card !== null}
      onClose={onClose}
      backLabel={`Back to ${listName}`}
      ariaLabelledBy="list-card-details-title"
    >
      {/* Quick Actions Bar - Star, List, Deck (always visible at top) */}
      {oracleId && (
        <div className="border-storm shrink-0 border-b">
          <QuickActionsBar oracleId={oracleId} cardName={cardName} card={scryfallCard} scryfallId={scryfallId ?? undefined} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Card Showcase (Hero) */}
        {scryfallCard ? <CardShowcase card={scryfallCard} /> : isLoadingScryfall ? <CardShowcaseSkeleton /> : null}

        {/* List Card Info Section */}
        {card && (
          <ListCardInfoSection
            card={card}
            onQuantityChange={handleQuantityChange}
            onPriorityChange={handlePriorityChange}
            onNotesChange={handleNotesChange}
            isUpdating={isUpdating}
          />
        )}

        {/* Edition Selector */}
        {card && scryfallId && (
          <EditionSelector
            cardName={cardName}
            currentScryfallId={scryfallId}
            onEditionChange={handleEditionChange}
            isUpdating={isUpdating}
            label="Edition"
            modalTitle="Change Edition"
            description="Change the specific printing for this list entry."
            showCardPreview={true}
          />
        )}

        {/* Availability Section */}
        {oracleId && <CardAvailabilitySection oracleId={oracleId} cardName={cardName} selectedScryfallId={scryfallId ?? undefined} />}

        {/* Prices Section */}
        {scryfallCard ? <PricesSection card={scryfallCard} /> : isLoadingScryfall ? <PricesSectionSkeleton /> : null}

        {/* Rulings Section */}
        {scryfallId && scryfallCard ? <RulingsSection cardId={scryfallId} /> : isLoadingScryfall ? <RulingsSectionSkeleton /> : null}

        {/* Legalities Section */}
        {scryfallCard && <LegalitiesSection card={scryfallCard} />}
      </div>

      {/* Footer */}
      <footer className="border-storm flex h-16 shrink-0 items-center justify-between border-t px-4">
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="text-danger hover:bg-danger/10 flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors"
        >
          <IoTrash className="h-4 w-4" />
          Remove from List
        </button>
        {scryfallCard && <CardDetailsScryfallLink set={scryfallCard.set} collectorNumber={scryfallCard.collector_number} />}
      </footer>

      {/* Delete Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Remove from List"
        description={
          <>
            Are you sure you want to remove <span className="text-pearl font-medium">{cardName}</span> from this list?
          </>
        }
        confirmLabel="Remove"
        pendingLabel="Removing..."
        isPending={isUpdating}
        onConfirm={handleRemove}
      />
    </CardDetailsPanelLayout>
  );
}
